import { MapMarker } from './types';
import { categoryIconHtml } from 'components/common/icons/categoryIcon';

/**
 * 지도 마커의 생김새 — 카카오와 구글이 같은 것을 씁니다.
 *
 * 카카오는 CustomOverlay, 구글은 OverlayView 로 띄우는 방식만 다르고
 * 안에 들어가는 HTML 은 같아야 합니다. 두 곳에 따로 적어 두면 한쪽만
 * 고치는 일이 생기고, 실제로 국내와 해외 지도의 마커 모양이 달랐습니다.
 *
 * 세 가지 모양이 있습니다.
 *   평점 칩  rating 이 있는 추천 장소 — 별점 알약
 *   목적지   isDestination — 기본 핀 + 다크 말풍선
 *   참여자   그 외 — 원형(프로필 사진 또는 이름 첫 글자)
 */

/** 마커 본체 HTML. 세 모양 중 하나를 돌려줍니다. */
export const markerContentHtml = (m: MapMarker): string => {
    if (m.rating != null && !m.isDestination) {
        const chipColor = m.color || '#1a73e8';
        const star = `<svg viewBox='0 0 24 24' width='10' height='10' fill='currentColor' aria-hidden><polygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2'/></svg>`;
        // 카테고리 키 → SVG 마크업 (이모지 금지). 키 없으면 prefix 생략.
        const iconPrefix = m.categoryIcon
            ? `<span style='display:inline-flex;align-items:center;line-height:1;'>${categoryIconHtml(m.categoryIcon, 11)}</span>`
            : '';
        return `<div style="
            display:inline-flex;align-items:center;gap:4px;
            padding:4px 9px;
            background:${chipColor};color:#fff;
            border-radius:14px;
            font-size:11px;font-weight:800;line-height:1;
            box-shadow:0 1px 4px rgba(0,0,0,.25);
            white-space:nowrap;cursor:pointer;
            transform:translateZ(0);will-change:transform;
        " title="${m.tooltip || ''}">${iconPrefix}${star}<span>${m.rating}</span></div>`;
    }

    const bgColor = m.color || '#4285F4';
    const innerContent = m.profileImgUrl
        ? `<img src="${m.profileImgUrl}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" />`
        : `<span style="color:#fff;font-size:14px;font-weight:700;">${m.label}</span>`;
    return `<div style="
        width:36px;height:36px;
        border-radius:50%;
        background:${m.profileImgUrl ? '#fff' : bgColor};
        display:flex;align-items:center;justify-content:center;
        border:3px solid ${bgColor};
        box-shadow:0 2px 8px rgba(0,0,0,.3);
        cursor:pointer;overflow:hidden;
        /* GPU 레이어 강제 — 원(벡터)+텍스트(글리프)를 한 덩어리로 합성해 분리 움직임 방지 */
        transform:translateZ(0);
        will-change:transform;
        backface-visibility:hidden;
    " title="${m.tooltip || m.label}">${innerContent}</div>`;
};

/** 목적지 말풍선 — 조금 크고 진합니다. */
export const destinationTooltipHtml = (text: string): string => `<div style="
    padding:4px 10px;
    background:#222;
    color:#fff;
    border-radius:6px;
    font-size:12px;
    font-weight:600;
    white-space:nowrap;
    box-shadow:0 2px 6px rgba(0,0,0,.3);
    transform:translateY(-8px);
">${text}</div>`;

/** 참여자 말풍선 — 닉네임과 거리. 상시 노출이라 한 단계 작습니다. */
export const memberTooltipHtml = (text: string): string => `<div style="
    padding:3px 8px;
    background:rgba(34,34,34,0.88);
    color:#fff;
    border-radius:6px;
    font-size:11px;
    font-weight:600;
    white-space:nowrap;
    box-shadow:0 2px 6px rgba(0,0,0,.25);
    pointer-events:none;
    transform:translateY(-4px);
">${text}</div>`;

/**
 * 내 위치 점 — 파란 원에 흰 테두리, 뒤에 옅은 테.
 *
 * 예전에는 14px 에 흰 테두리 3px 라 파란 알맹이가 8px 뿐이었고, 지도 위 글자와
 * 길 선에 묻혀 거의 보이지 않았습니다. 알맹이를 20px 로 키우고 뒤에 옅은 테를
 * 둡니다 — 크기만 키우는 것보다 "여기가 나" 로 읽힙니다.
 *
 * 부르는 쪽이 가운데를 맞춰 줍니다(카카오 xAnchor/yAnchor 0.5, Leaflet 은
 * translate(-50%,-50%)). 그래서 바깥 상자가 커져도 자리는 그대로입니다.
 */
export const myLocationHtml = (): string =>
    `<div style="width:40px;height:40px;border-radius:50%;background:rgba(66,133,244,0.18);display:flex;align-items:center;justify-content:center;">`
    + `<div style="width:20px;height:20px;border-radius:50%;background:#4285F4;border:3px solid #fff;box-sizing:border-box;box-shadow:0 1px 4px rgba(0,0,0,0.35);"></div>`
    + `</div>`;

/* ══════════════════════════════════════════════════════════════
 * 장소 고르기 지도의 마커 — 카카오와 구글이 같은 것을 씁니다.
 *
 * 예전에는 카카오는 PlaceMapView 안 인라인 HTML, 구글은 GoogleMap 안
 * SVG data-URL 로 각자 그려서 국내와 해외의 마커가 아예 다른 모양이었습니다.
 * 모양 자체는 지금 카카오가 그리는 것을 그대로 옮겼습니다 — 보이는 것은
 * 바뀌지 않고, 이제 해외도 같은 것을 씁니다.
 * ══════════════════════════════════════════════════════════════ */

/** 마커에 필요한 것만. MapPlace 의 부분집합입니다. */
export interface PlaceMarkerInput {
    name?: string;
    rating?: number | string | null;
    imageUrl?: string;
    categoryIcon?: string;
    priceLabel?: string;
}

export type PlaceMarkerMode = 'simple' | 'attraction' | 'hotel';

const esc = (v?: string): string =>
    (v || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const STAR = `<svg viewBox='0 0 24 24' width='10' height='10' fill='currentColor' aria-hidden><polygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2'/></svg>`;

/**
 * 네 가지 모양
 *   고른 것   다크 칩 — 사진 + 이름 + 평점
 *   평점 있음 파란 알약 — 카테고리 아이콘 + 별 + 점수
 *   숙소 가격 흰 라벨 — 1박 가격 (markerMode 가 hotel 이고 가격이 있을 때)
 *   그 외     흰 라벨 — 이름
 */
export const placeMarkerHtml = (
    place: PlaceMarkerInput,
    isSelected: boolean,
    mode: PlaceMarkerMode = 'simple',
): string => {
    const name = esc(place.name);
    const ratingText = place.rating != null && place.rating !== '' ? esc(String(place.rating)) : '';

    if (isSelected) {
        const photo = place.imageUrl
            ? `<img src="${esc(place.imageUrl)}" alt="" style="width:24px;height:24px;border-radius:6px;object-fit:cover;margin-right:4px;flex-shrink:0;" />`
            : '';
        const rating = ratingText ? `${STAR}<span style='color:#FFD54F'>${ratingText}</span>` : '';
        return `<div style="display:inline-flex;align-items:center;gap:5px;padding:5px 11px;background:#222;color:#fff;border-radius:16px;font-size:12px;font-weight:700;line-height:1;box-shadow:0 2px 8px rgba(0,0,0,.35);white-space:nowrap;border:2px solid #fff;">${photo}<span>${name}</span>${rating}</div>`;
    }

    if (ratingText) {
        // 카테고리 키 → SVG 마크업 (이모지 금지). 키 없으면 prefix 생략.
        const icon = place.categoryIcon
            ? `<span style="display:inline-flex;align-items:center;line-height:1;">${categoryIconHtml(place.categoryIcon, 11)}</span>`
            : '';
        return `<div style="display:inline-flex;align-items:center;gap:3px;padding:4px 8px;background:#1a73e8;color:#fff;border-radius:14px;font-size:11px;font-weight:800;line-height:1;box-shadow:0 1px 4px rgba(0,0,0,.25);white-space:nowrap;">${icon}${STAR}<span>${ratingText}</span></div>`;
    }

    const label = mode === 'hotel' && place.priceLabel ? esc(place.priceLabel) : name;
    return `<div style="background:rgba(255,255,255,0.95);padding:3px 8px;border-radius:10px;font-size:10px;font-weight:700;color:#333;white-space:nowrap;box-shadow:0 1px 3px rgba(0,0,0,0.15);border:1px solid #e0e0e0;">${label}</div>`;
};
